import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { signOut, initials, fullName, resetPasswordForEmail } from '../lib/auth'
import { upsertProfile } from '../lib/profiles'
import { useAuth } from '../context/AuthContext'
import { showToast } from '../components/Toast'
import PhoneInput, { isValidPhoneE164 } from '../components/PhoneInput'
import MembershipCard from '../components/MembershipCard'
import { InstallAppButton } from '../components/InstallPrompt'

const SUPPORT_PHONE = '+19549009969'
const SUPPORT_WHATSAPP = '19549009969'

// Account — spec §6.
//   Avatar with initials · name · email · phone
//   Ghost Members card (navy)
//   Saved address · Phone & email · Password · Help · Sign out
export default function Account() {
  const navigate = useNavigate()
  const { user, profile, refresh } = useAuth()

  const [f, setF] = useState(() => hydrate(profile, user))
  const [savingSection, setSavingSection] = useState(null)

  useEffect(() => { setF(hydrate(profile, user)) }, [profile, user])

  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }))

  async function saveSection(section, patch) {
    if (!user) return
    setSavingSection(section)
    try {
      await upsertProfile(user.id, patch)
      await refresh()
      showToast('Saved')
    } catch (err) {
      showToast(err?.message || 'Save failed')
    } finally {
      setSavingSection(null)
    }
  }

  async function handleLogout() {
    try { await signOut() } catch { /* ignore */ }
    navigate('/login')
  }

  async function handlePasswordReset() {
    if (!user?.email) return
    try {
      await resetPasswordForEmail(user.email)
      showToast('Reset link sent — check your email')
    } catch (err) {
      showToast(err?.message || 'Could not send reset email')
    }
  }

  return (
    <Layout>
      <div className="px-5 pt-5">
        {/* ── Identity strip ─────────────────────────────────────────── */}
        <section className="flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-navy font-bricolage text-[18px] font-extrabold text-white">
            {initials(profile)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bricolage text-[16px] font-extrabold text-navy">
              {fullName(profile) || user?.email || 'You'}
            </div>
            <div className="truncate text-[12px] text-ink-2">{user?.email}</div>
            {profile?.phone && (
              <div className="text-[12px] text-ink-2">{profile.phone}</div>
            )}
          </div>
        </section>

        {/* ── Ghost Members card (Stripe-wired, source of truth = webhook) */}
        <MembershipCard />
      </div>

      <div className="mt-4 space-y-3 px-4">
        {/* ── Saved address (with hotel + room) ──────────────────────── */}
        <ExpandableCard title="Saved address">
          <AddressForm
            f={f}
            set={set}
            saving={savingSection === 'address'}
            onSave={() => saveSection('address', {
              address_line1: f.street,
              address_line2: f.apt,
              city: f.city,
              state: f.state,
              zip: f.zip,
              hotel_name: f.isHotel ? f.hotelName : '',
              room_number: f.isHotel ? f.roomNumber : '',
            })}
          />
        </ExpandableCard>

        {/* ── Phone & email ──────────────────────────────────────────── */}
        <ExpandableCard title="Phone & email">
          <ContactForm
            f={f}
            set={set}
            userEmail={user?.email}
            saving={savingSection === 'contact'}
            onSave={() => {
              if (f.phone && !isValidPhoneE164(f.phone)) {
                showToast('Phone number is invalid')
                return
              }
              saveSection('contact', {
                first_name: f.firstName,
                last_name: f.lastName,
                phone: f.phone,
              })
            }}
          />
        </ExpandableCard>

        {/* ── Password ───────────────────────────────────────────────── */}
        <ExpandableCard title="Password">
          <p className="text-[12px] text-ink-2">
            We&apos;ll send a reset link to <b className="text-navy">{user?.email}</b>.
          </p>
          <button
            type="button"
            onClick={handlePasswordReset}
            className="mt-3 w-full rounded-lg bg-navy px-3 py-2 text-[12px] font-extrabold text-white"
          >
            Send password reset link
          </button>
        </ExpandableCard>

        {/* ── Help ───────────────────────────────────────────────────── */}
        <ExpandableCard title="Help">
          <p className="text-[12px] text-ink-2">
            Reach the Ghost Laundry team any time — usually under 15 min during business hours.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <a
              href={`https://wa.me/${SUPPORT_WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-navy bg-white py-2 text-center text-[12px] font-extrabold text-navy"
            >
              💬 WhatsApp
            </a>
            <a
              href={`tel:${SUPPORT_PHONE}`}
              className="rounded-lg bg-navy py-2 text-center text-[12px] font-extrabold text-white"
            >
              📞 Call
            </a>
          </div>
        </ExpandableCard>

        <InstallAppButton />

        {/* ── Sign out ───────────────────────────────────────────────── */}
        <div className="pt-2 text-center">
          <button
            type="button"
            onClick={handleLogout}
            className="text-[12px] font-bold text-red"
          >
            Sign out
          </button>
        </div>
      </div>
    </Layout>
  )
}

// ── Expandable card ──────────────────────────────────────────────────

function ExpandableCard({ title, children }) {
  return (
    <details className="overflow-hidden rounded-xl border border-line bg-white">
      <summary className="flex cursor-pointer items-center justify-between px-4 py-3 text-[13px] font-extrabold text-navy">
        <span>{title}</span>
        <span aria-hidden className="text-[10px] text-ink-3">▾</span>
      </summary>
      <div className="border-t border-line p-4">{children}</div>
    </details>
  )
}

// ── Forms ────────────────────────────────────────────────────────────

function AddressForm({ f, set, saving, onSave }) {
  return (
    <>
      <label className="mb-2 flex items-center gap-2 text-[11px] text-navy">
        <input
          type="checkbox"
          checked={!!f.isHotel}
          onChange={(e) => {
            const on = e.target.checked
            set('isHotel', on)
            if (!on) { set('hotelName', ''); set('roomNumber', '') }
          }}
          className="h-4 w-4 rounded border-line"
        />
        I&apos;m staying in a hotel
      </label>
      {f.isHotel && (
        <input
          className="field-input mb-2"
          placeholder="Hotel name"
          value={f.hotelName}
          onChange={(e) => set('hotelName', e.target.value)}
        />
      )}
      <input
        className="field-input"
        placeholder="Street address"
        value={f.street}
        onChange={(e) => set('street', e.target.value)}
      />
      {f.isHotel ? (
        <input
          className="field-input mt-2"
          placeholder="Room number"
          value={f.roomNumber}
          onChange={(e) => set('roomNumber', e.target.value)}
        />
      ) : (
        <input
          className="field-input mt-2"
          placeholder="Apt / Suite (optional)"
          value={f.apt}
          onChange={(e) => set('apt', e.target.value)}
        />
      )}
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input className="field-input" placeholder="City" value={f.city} onChange={(e) => set('city', e.target.value)} />
        <input className="field-input" placeholder="State" value={f.state} onChange={(e) => set('state', e.target.value)} />
      </div>
      <input className="field-input mt-2" placeholder="ZIP" value={f.zip} onChange={(e) => set('zip', e.target.value)} />
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="mt-3 w-full rounded-lg bg-navy px-3 py-2 text-[12px] font-extrabold text-white disabled:opacity-60"
      >
        {saving ? 'Saving…' : 'Save address'}
      </button>
    </>
  )
}

function ContactForm({ f, set, userEmail, saving, onSave }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <input className="field-input" placeholder="First name" value={f.firstName} onChange={(e) => set('firstName', e.target.value)} />
        <input className="field-input" placeholder="Last name" value={f.lastName} onChange={(e) => set('lastName', e.target.value)} />
      </div>
      <input className="field-input mt-2" placeholder="Email" value={userEmail || ''} disabled />
      <p className="mt-1 text-[10px] text-ink-3">
        Email is managed by your login — change it via Supabase Auth.
      </p>
      <div className="mt-2">
        <PhoneInput value={f.phone} onChange={(v) => set('phone', v)} placeholder="Phone / WhatsApp" />
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="mt-3 w-full rounded-lg bg-navy px-3 py-2 text-[12px] font-extrabold text-white disabled:opacity-60"
      >
        {saving ? 'Saving…' : 'Save contact info'}
      </button>
    </>
  )
}

function hydrate(profile, user) {
  return {
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    email: profile?.email || user?.email || '',
    phone: profile?.phone || '',
    street: profile?.address_line1 || '',
    apt: profile?.address_line2 || '',
    zip: profile?.zip || '',
    city: profile?.city || '',
    state: profile?.state || '',
    hotelName: profile?.hotel_name || '',
    roomNumber: profile?.room_number || '',
    isHotel: !!profile?.hotel_name,
  }
}
